import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    const removeSsoParam = () => {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('sso')) {
        return;
      }
      url.searchParams.delete('sso');
      const newSearch = url.searchParams.toString();
      const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash;
      window.history.replaceState({}, document.title, newUrl);
    };

    const trySsoLogin = async () => {
      const url = new URL(window.location.href);
      const ssoToken = url.searchParams.get('sso');
      if (!ssoToken) {
        return false;
      }
      const success = await authService.loginWithSsoToken(ssoToken);
      removeSsoParam();
      return success;
    };

    const checkAuthentication = async () => {
      try {
        const ssoHandled = await trySsoLogin();
        if (ssoHandled) {
          if (isMounted) {
            setIsAuthenticated(true);
          }
          return;
        }

        const hasSession = authService.isAuthenticated();
        if (!hasSession) {
          if (isMounted) {
            setIsAuthenticated(false);
          }
          return;
        }

        const isValid = await authService.checkAuth();
        if (isMounted) {
          setIsAuthenticated(isValid);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        if (isMounted) {
          setIsAuthenticated(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuthentication();

    const unsubscribe = authService.onAuthStateChange((authenticated) => {
      if (isMounted) {
        setIsAuthenticated(authenticated);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [location.search]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render protected content
  return children;
};

export default ProtectedRoute;


